<script setup lang="ts">
import { computed } from 'vue'
import { useMessage } from 'naive-ui'
import { RouterLink } from 'vue-router'
import { useWikiMarkdown } from '../composables/useWikiMarkdown'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import { agentCostLabel } from './AgentCostLabel'
import type { ChatMessage } from '../stores/agent'
import { isInterAgentTurn, sourceLabel } from '../lib/interAgentTurn'

// One turn of the /agent transcript. Your own turns are right-aligned
// bubbles; the agent's output reads as plain left-aligned prose (markdown);
// a turn another agent delivered (#1837) is a dashed note naming its sender,
// so it is never mistaken for yours; system notes sit centred and small.
const props = defineProps<{
  message: ChatMessage
  // How many identical consecutive turns this one stands for (#1442).
  repeatCount: number
}>()

const wikiMarkdown = useWikiMarkdown()
const toast = useMessage()
const { formatDate, formatAbsoluteDate } = useFormattedDate()
// Ticks so the relative timestamp keeps advancing while idle (#1438).
const now = useNow()

const interAgent = computed(() => isInterAgentTurn(props.message))
const own = computed(() => props.message.role === 'you' && !interAgent.value)
const kind = computed(() => {
  if (own.value) return 'you'
  return interAgent.value ? 'inter-agent' : 'agent'
})

const ROW_CLASS = { you: 'justify-end', 'inter-agent': 'justify-start', agent: 'justify-start' } as const
const BODY_CLASS = {
  you: 'max-w-[78%] rounded-[14px_14px_4px_14px] border border-border-subtle bg-surface-hover px-3.5 py-2.5 text-ink',
  'inter-agent': 'w-full rounded-xl border border-dashed border-border-control px-3.5 py-2.5 text-ink-muted',
  agent: 'w-full max-w-[72ch] py-1 text-ink-reading',
} as const

// Compact "context so far" label (#886) — a signal for when to run /compact.
function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`
  return String(n)
}

// Long agent responses are tedious to text-select inside a scrolling log.
async function copyMessage(): Promise<void> {
  try {
    await navigator.clipboard.writeText(props.message.text)
    toast.success('Copied to clipboard')
  } catch {
    toast.warning('Clipboard not available — select and copy manually')
  }
}
</script>

<template>
  <div class="flex" :class="message.role === 'system' ? 'justify-center' : ROW_CLASS[kind]" data-testid="agent-message">
    <div
      v-if="message.role === 'system'"
      class="max-w-[85%] rounded-lg border border-border-subtle bg-surface-raised px-3 py-1 text-xs text-ink-muted italic"
      data-testid="agent-message-system"
    >
      <span class="sr-only">System note:</span>
      {{ message.text }}
    </div>
    <div
      v-else
      class="group text-sm leading-relaxed break-words"
      :class="BODY_CLASS[kind]"
      :data-testid="`agent-message-${kind}`"
    >
      <div v-if="interAgent" class="mb-1 flex flex-wrap items-center gap-2 text-xs" data-testid="inter-agent-badge">
        <span class="font-semibold text-attention">{{ message.sender_name }} →</span>
        <span class="text-ink-faint">{{ sourceLabel(message.source) }}</span>
        <RouterLink
          v-if="message.channel"
          :to="{ path: '/channels', query: { channel: String(message.channel.id) } }"
          class="text-ink-muted underline"
          data-testid="inter-agent-channel-link"
          >#{{ message.channel.name }}</RouterLink
        >
        <span v-else-if="message.channel_deleted" class="italic" data-testid="inter-agent-channel-deleted"
          >deleted channel</span
        >
      </div>
      <span class="sr-only">{{ own ? 'You said:' : 'Agent said:' }}</span>
      <span v-if="message.role === 'you'" class="whitespace-pre-wrap">{{ message.text }}</span>
      <article v-else class="wiki-markdown agent-prose overflow-x-auto" v-html="wikiMarkdown.render(message.text)" />

      <!-- Per-turn footer: relative time, repeat count (#1442), context size
           (#886) and cost (#654) on agent turns, and a Copy affordance —
           hover-revealed on desktop, always visible on mobile. -->
      <div
        v-if="message.created_at || message.role === 'agent'"
        class="mt-1 flex items-center gap-3 text-[11px] text-ink-faint"
        :class="own ? 'justify-end' : 'justify-between'"
      >
        <div class="flex items-center gap-2">
          <time
            v-if="message.created_at"
            :datetime="message.created_at"
            :title="formatAbsoluteDate(message.created_at)"
            class="tabular-nums"
            data-testid="agent-message-time"
            >{{ formatDate(message.created_at, now) }}</time
          >
          <span
            v-if="repeatCount > 1"
            class="tabular-nums"
            :title="`This message repeated ${repeatCount} times in a row`"
            data-testid="agent-message-repeat-count"
            >×{{ repeatCount }}</span
          >
          <span
            v-if="message.role === 'agent' && message.context_tokens != null"
            class="tabular-nums"
            :title="`~${message.context_tokens.toLocaleString()} tokens in context this turn`"
            data-testid="agent-message-context-tokens"
            >~{{ formatTokens(message.context_tokens) }} ctx</span
          >
          <span
            v-if="message.role === 'agent' && message.cost_cents != null"
            class="tabular-nums"
            title="Estimated cost of this turn"
            data-testid="agent-message-cost"
            >{{ agentCostLabel(message.cost_cents) }}</span
          >
        </div>
        <button
          v-if="message.role === 'agent'"
          type="button"
          class="min-h-6 rounded px-1.5 transition-opacity hover:text-ink sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100"
          aria-label="Copy agent message"
          data-testid="agent-message-copy"
          @click="copyMessage"
        >
          Copy
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Agent output reads as chat, not as a wiki page: the shared .wiki-markdown
   rules set a 19px serif reading face and page-sized headings, so this
   steps them down to the transcript's 14px sans (higher specificity than
   the unlayered .wiki-markdown rules, without touching them). */
.agent-prose {
  font-family: var(--font-sans);
  font-size: 14px;
  line-height: 1.6;
}
.agent-prose :deep(p),
.agent-prose :deep(ul),
.agent-prose :deep(ol) {
  margin-block: 0.5rem;
}
.agent-prose :deep(> :first-child) {
  margin-top: 0;
}
.agent-prose :deep(> :last-child) {
  margin-bottom: 0;
}
.agent-prose :deep(h1),
.agent-prose :deep(h2),
.agent-prose :deep(h3) {
  margin: 1rem 0 0.5rem;
  font-family: var(--font-sans);
  font-size: 15px;
  line-height: 1.4;
}
.agent-prose :deep(pre) {
  margin-block: 0.75rem;
  padding: 0.75rem;
  font-size: 12px;
}
.agent-prose :deep(code) {
  font-size: 12.5px;
}
</style>
